/**
 * QP-172 — Portfolio List component.
 *
 * Display-only component listing portfolios with payment details.
 * Clicking a portfolio navigates to its tab. Renders on both global and
 * goal pages; the entire component is hidden only when there are no
 * portfolios to show.
 */

import { VisualizationContainer } from "@/components/flow/visualization-container";
import { useFlowInstancePortfoliosForGoals } from "@/lib/hooks/use-flow-instances/use-portfolios";
import type { PortfolioListConfig } from "@/lib/types/flow";
import { PortfolioGroupView } from "./portfolio-list-group";
import type { PortfolioListGoal, PortfoliosForGoals } from "./types";

type PortfolioListProps = {
  instanceId: string;
  /** When set, show only this goal's portfolios (goal-page mode). */
  goalId?: string | null;
  /** All goals for the instance. */
  goals: PortfolioListGoal[];
  config: PortfolioListConfig;
  language: string;
};

/**
 * Advisor-side Portfolio List entry point.
 * Loads the portfolios it shows with one request (QL-676) and hands them to
 * the shared visibility wrapper.
 */
export function PortfolioList({ instanceId, goals, goalId, config, language }: PortfolioListProps) {
  const relevantGoals = goalId ? goals.filter((g) => g.id === goalId) : goals;
  // One portfolios request whatever the goal count (QL-676): the instance-wide
  // read on a global page, the goal context's own read on a goal page.
  const results = useFlowInstancePortfoliosForGoals(
    instanceId,
    relevantGoals.map((g) => g.id),
  );

  return (
    <PortfolioListWithVisibility
      goals={relevantGoals}
      allGoals={relevantGoals}
      showGoalName={!goalId}
      showAmount={config.showAmount !== false}
      showFrequency={config.showFrequency !== false}
      showDates={config.showDates !== false}
      language={language}
      config={config}
      results={results}
    />
  );
}

/**
 * Shared visibility-aware wrapper used by both advisor and public variants.
 * Each surface loads `results` (one entry per `allGoals` item) from its own
 * data source.
 *
 * Checks the total portfolio count and renders the full container whenever at
 * least one portfolio exists. Returns null otherwise.
 */
export function PortfolioListWithVisibility({
  goals,
  allGoals,
  showGoalName,
  showAmount,
  showFrequency,
  showDates,
  language,
  config,
  results,
}: {
  goals: PortfolioListGoal[];
  allGoals: PortfolioListGoal[];
  showGoalName: boolean;
  showAmount: boolean;
  showFrequency: boolean;
  showDates: boolean;
  language: string;
  config: PortfolioListConfig;
  results: PortfoliosForGoals;
}) {
  const anyLoading = results.some((r) => r.isLoading);
  if (anyLoading) return null;

  const totalPortfolios = results.reduce((sum, r) => sum + r.portfolios.length, 0);
  if (totalPortfolios === 0) return null;

  const portfolioMap = new Map(results.map((r, i) => [allGoals[i].id, r.portfolios]));

  // On goal pages (goals is a subset of allGoals), hide if this goal has no portfolios.
  const displayedPortfolios = goals.reduce(
    (sum, g) => sum + (portfolioMap.get(g.id)?.length ?? 0),
    0,
  );
  if (displayedPortfolios === 0) return null;

  return (
    <VisualizationContainer config={config}>
      <div className="space-y-4">
        {goals.map((goal) => {
          const portfolios = portfolioMap.get(goal.id) ?? [];
          if (portfolios.length === 0) return null;
          return (
            <PortfolioGroupView
              key={goal.id}
              goal={goal}
              portfolios={portfolios}
              showGoalName={showGoalName}
              showAmount={showAmount}
              showFrequency={showFrequency}
              showDates={showDates}
              language={language}
            />
          );
        })}
      </div>
    </VisualizationContainer>
  );
}
